import { useState, useEffect } from 'react';
import { salesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    FileCheck, 
    Loader2,
    CheckCircle2,
    XCircle,
    Clock,
    Calendar,
    AlertTriangle,
    ArrowUpDown
} from 'lucide-react';

const chequeStatuses = [
    { value: 'pending', label: 'Pending', icon: Clock, color: 'bg-[#E9C46A] text-[#1A1A1A]' },
    { value: 'deposited', label: 'Deposited', icon: ArrowUpDown, color: 'bg-[#264653] text-white' },
    { value: 'cleared', label: 'Cleared', icon: CheckCircle2, color: 'bg-[#2A9D8F] text-white' },
    { value: 'bounced', label: 'Bounced', icon: XCircle, color: 'bg-[#E76F51] text-white' },
];

const Cheques = () => {
    const [cheques, setCheques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState('deposit_date_desc');
    const [selectedCheque, setSelectedCheque] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchCheques();
    }, [filter, sortBy]);

    const fetchCheques = async () => {
        setLoading(true);
        try {
            const status = filter === 'all' ? null : filter;
            const response = await salesAPI.getCheques(status, sortBy);
            // Flatten the merged sale list into one row per cheque so the
            // table renders the bank / cheque-number / deposit-date for
            // BOTH single-cheque sales and every cheque inside a split sale.
            // For split sales the API returns the sale wrapper with an
            // embedded `cheques[]` array — we expand each entry into its
            // own row, while single-cheque sales are emitted once with the
            // legacy cheque_* columns.
            const rows = [];
            for (const sale of response.data) {
                const embedded = Array.isArray(sale.cheques) ? sale.cheques : [];
                if (sale.payment_method === 'split' && embedded.length > 0) {
                    for (const ch of embedded) {
                        // Frontend status filter (the backend already filtered
                        // by `cheques.status` when status != null, but for the
                        // "all" tab a split sale may have a mix of statuses).
                        if (status && (ch.status || 'pending') !== status) continue;
                        rows.push({
                            // unique row id = cheque.id (sales w/ multiple cheques
                            // would otherwise collide on React keys)
                            row_id: ch.id,
                            // parent invoice context
                            sale_id: sale.id,
                            invoice_number: sale.invoice_number,
                            company_name: sale.company_name,
                            created_at: sale.created_at,
                            // cheque-level fields (rendered in the table)
                            amount: ch.amount,
                            cheque_bank_name: ch.bank_name,
                            cheque_number: ch.cheque_number,
                            cheque_deposit_date: ch.deposit_date,
                            cheque_status: ch.status || 'pending',
                            // discriminator for the Update Status dispatch
                            _is_split_line: true,
                            _cheque_id: ch.id,
                        });
                    }
                } else {
                    rows.push({
                        row_id: sale.id,
                        sale_id: sale.id,
                        invoice_number: sale.invoice_number,
                        company_name: sale.company_name,
                        created_at: sale.created_at,
                        amount: sale.amount,
                        cheque_bank_name: sale.cheque_bank_name,
                        cheque_number: sale.cheque_number,
                        cheque_deposit_date: sale.cheque_deposit_date,
                        cheque_status: sale.cheque_status || 'pending',
                        _is_split_line: false,
                    });
                }
            }
            setCheques(rows);
        } catch (error) {
            console.error('Failed to fetch cheques:', error);
            toast.error('Failed to load cheques');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedCheque || !newStatus) return;
        
        setProcessing(true);
        try {
            // Dispatch to the right endpoint depending on whether this row
            // represents a single-cheque sale (legacy fields on the sale doc)
            // or one line inside a split-payment sale's `cheques[]` array.
            if (selectedCheque._is_split_line) {
                await salesAPI.updateChequeStatus(
                    selectedCheque.sale_id,
                    selectedCheque._cheque_id,
                    newStatus,
                );
            } else {
                await salesAPI.update(selectedCheque.sale_id, {
                    cheque_status: newStatus,
                });
            }
            toast.success(`Cheque status updated to ${newStatus}`);
            setSelectedCheque(null);
            setNewStatus('');
            fetchCheques();
        } catch (error) {
            toast.error('Failed to update cheque status');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusBadge = (status) => {
        const statusInfo = chequeStatuses.find(s => s.value === status) || chequeStatuses[0];
        return (
            <Badge className={`${statusInfo.color} font-medium flex items-center gap-1 w-fit`}>
                <statusInfo.icon size={12} />
                {statusInfo.label}
            </Badge>
        );
    };

    const isOverdue = (depositDate, status) => {
        if (status !== 'pending') return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deposit = new Date(depositDate);
        return deposit < today;
    };

    const getDaysUntilDeposit = (depositDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deposit = new Date(depositDate);
        const diffTime = deposit - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getPendingDays = (createdAt) => {
        const created = new Date(createdAt);
        const today = new Date();
        const diffTime = Math.abs(today - created);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getPendingDaysBadge = (days) => {
        if (days <= 7) {
            return <Badge className="bg-green-100 text-green-700">{days} days</Badge>;
        } else if (days <= 30) {
            return <Badge className="bg-yellow-100 text-yellow-700">{days} days</Badge>;
        } else {
            return <Badge className="bg-red-100 text-red-700">{days} days</Badge>;
        }
    };

    // Calculate summary stats
    const totalPending = cheques.filter(c => c.cheque_status === 'pending' || !c.cheque_status).reduce((sum, c) => sum + c.amount, 0);
    const totalDeposited = cheques.filter(c => c.cheque_status === 'deposited').reduce((sum, c) => sum + c.amount, 0);
    const totalCleared = cheques.filter(c => c.cheque_status === 'cleared').reduce((sum, c) => sum + c.amount, 0);
    const totalBounced = cheques.filter(c => c.cheque_status === 'bounced').reduce((sum, c) => sum + c.amount, 0);

    if (loading && cheques.length === 0) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    return (
        <div data-testid="cheques-page" className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Cheque Management</h1>
                <p className="text-gray-500 mt-1">Track and manage all cheque payments</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <Clock className="text-[#E9C46A]" size={20} />
                            <span className="text-sm text-gray-500">Pending</span>
                        </div>
                        <p className="text-xl font-bold text-[#1A1A1A] euro-amount">
                            {formatCurrency(totalPending)}
                        </p>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <ArrowUpDown className="text-[#264653]" size={20} />
                            <span className="text-sm text-gray-500">Deposited</span>
                        </div>
                        <p className="text-xl font-bold text-[#1A1A1A] euro-amount">
                            {formatCurrency(totalDeposited)}
                        </p>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <CheckCircle2 className="text-[#2A9D8F]" size={20} />
                            <span className="text-sm text-gray-500">Cleared</span>
                        </div>
                        <p className="text-xl font-bold text-[#2A9D8F] euro-amount">
                            {formatCurrency(totalCleared)}
                        </p>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <XCircle className="text-[#E76F51]" size={20} />
                            <span className="text-sm text-gray-500">Bounced</span>
                        </div>
                        <p className="text-xl font-bold text-[#E76F51] euro-amount">
                            {formatCurrency(totalBounced)}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Cheques Table */}
            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <CardTitle className="flex items-center gap-2">
                            <FileCheck className="text-[#8B5CF6]" size={20} />
                            All Cheques ({cheques.length})
                        </CardTitle>
                        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger
                                    className="w-[210px] bg-white border-[#E5E5E5]"
                                    data-testid="cheques-sort-select"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="deposit_date_desc" data-testid="cheques-sort-deposit-desc">Deposit Date — Newest</SelectItem>
                                    <SelectItem value="deposit_date_asc"  data-testid="cheques-sort-deposit-asc">Deposit Date — Oldest</SelectItem>
                                    <SelectItem value="created_at_desc"   data-testid="cheques-sort-created-desc">Creation Date — Newest</SelectItem>
                                    <SelectItem value="created_at_asc"    data-testid="cheques-sort-created-asc">Creation Date — Oldest</SelectItem>
                                    <SelectItem value="amount_desc"       data-testid="cheques-sort-amount-desc">Amount — High → Low</SelectItem>
                                    <SelectItem value="amount_asc"        data-testid="cheques-sort-amount-asc">Amount — Low → High</SelectItem>
                                    <SelectItem value="cheque_number_asc" data-testid="cheques-sort-number-asc">Cheque No. — A → Z</SelectItem>
                                </SelectContent>
                            </Select>
                            <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList className="bg-[#F5F5F5]">
                                <TabsTrigger value="all" data-testid="filter-all" className="data-[state=active]:bg-white">
                                    All
                                </TabsTrigger>
                                <TabsTrigger value="pending" data-testid="filter-pending" className="data-[state=active]:bg-white">
                                    Pending
                                </TabsTrigger>
                                <TabsTrigger value="deposited" data-testid="filter-deposited" className="data-[state=active]:bg-white">
                                    Deposited
                                </TabsTrigger>
                                <TabsTrigger value="cleared" data-testid="filter-cleared" className="data-[state=active]:bg-white">
                                    Cleared
                                </TabsTrigger>
                                <TabsTrigger value="bounced" data-testid="filter-bounced" className="data-[state=active]:bg-white">
                                    Bounced
                                </TabsTrigger>
                            </TabsList>
                            </Tabs>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {cheques.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FileCheck size={48} className="mb-3 opacity-50" />
                            <p className="text-lg font-medium text-[#1A1A1A]">No cheques found</p>
                            <p className="text-sm">
                                {filter === 'all' 
                                    ? 'No cheque payments have been recorded yet'
                                    : `No cheques with "${filter}" status`
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#F5F5F5]">
                                        <TableHead className="font-medium">Invoice</TableHead>
                                        <TableHead className="font-medium">Amount</TableHead>
                                        <TableHead className="font-medium">Bank</TableHead>
                                        <TableHead className="font-medium">Cheque No.</TableHead>
                                        <TableHead className="font-medium">Deposit Date</TableHead>
                                        <TableHead className="font-medium">Pending Days</TableHead>
                                        <TableHead className="font-medium">Status</TableHead>
                                        <TableHead className="text-right font-medium">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {cheques.map((cheque) => {
                                        const overdue = isOverdue(cheque.cheque_deposit_date, cheque.cheque_status || 'pending');
                                        const daysUntil = getDaysUntilDeposit(cheque.cheque_deposit_date);
                                        const pendingDays = getPendingDays(cheque.created_at);
                                        
                                        return (
                                            <TableRow 
                                                key={cheque.row_id}
                                                className={`hover:bg-[#FAFAFA] ${overdue ? 'bg-red-50' : ''}`}
                                            >
                                                <TableCell className="font-mono text-sm font-medium">
                                                    {cheque.invoice_number}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                                        {formatCurrency(cheque.amount)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-gray-700">
                                                    {cheque.cheque_bank_name || '-'}
                                                </TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    {cheque.cheque_number || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar size={14} className="text-gray-400" />
                                                        <span className={overdue ? 'text-red-600 font-medium' : ''}>
                                                            {formatDate(cheque.cheque_deposit_date)}
                                                        </span>
                                                        {overdue && (
                                                            <AlertTriangle size={14} className="text-red-500" />
                                                        )}
                                                        {!overdue && daysUntil === 0 && cheque.cheque_status === 'pending' && (
                                                            <Badge className="bg-[#E9C46A] text-[#1A1A1A] text-xs">Today</Badge>
                                                        )}
                                                        {!overdue && daysUntil > 0 && daysUntil <= 3 && cheque.cheque_status === 'pending' && (
                                                            <Badge className="bg-blue-100 text-blue-700 text-xs">{daysUntil}d</Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Clock size={14} className="text-gray-400" />
                                                        {getPendingDaysBadge(pendingDays)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(cheque.cheque_status || 'pending')}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        data-testid={`update-cheque-${cheque.row_id}`}
                                                        onClick={() => {
                                                            setSelectedCheque(cheque);
                                                            setNewStatus(cheque.cheque_status || 'pending');
                                                        }}
                                                        variant="outline"
                                                        size="sm"
                                                        className="rounded-full"
                                                    >
                                                        Update Status
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Update Status Dialog */}
            <Dialog open={!!selectedCheque} onOpenChange={() => setSelectedCheque(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Update Cheque Status</DialogTitle>
                    </DialogHeader>
                    
                    {selectedCheque && (
                        <div className="space-y-6 py-4">
                            <div className="bg-[#F5F5F5] rounded-xl p-4 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Invoice</span>
                                    <span className="font-mono font-medium">{selectedCheque.invoice_number}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Amount</span>
                                    <span className="text-xl font-bold text-[#1A1A1A] euro-amount">
                                        {formatCurrency(selectedCheque.amount)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Bank</span>
                                    <span className="font-medium">{selectedCheque.cheque_bank_name || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Cheque No.</span>
                                    <span className="font-mono">{selectedCheque.cheque_number || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Deposit Date</span>
                                    <span className="font-medium">
                                        {formatDate(selectedCheque.cheque_deposit_date)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Given Date</span>
                                    <span className="font-medium">
                                        {formatDate(selectedCheque.created_at)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Pending Days</span>
                                    <span className="font-medium text-[#8B5CF6]">
                                        {getPendingDays(selectedCheque.created_at)} days
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>New Status</Label>
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                    <SelectTrigger data-testid="cheque-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {chequeStatuses.map((status) => (
                                            <SelectItem key={status.value} value={status.value}>
                                                <div className="flex items-center gap-2">
                                                    <status.icon size={16} />
                                                    {status.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {newStatus === 'bounced' && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-700">
                                        <AlertTriangle size={14} className="inline mr-1" />
                                        Marking as bounced will flag this payment for follow-up.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setSelectedCheque(null)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="confirm-status-btn"
                            onClick={handleUpdateStatus}
                            disabled={processing}
                            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Updating...
                                </>
                            ) : (
                                'Update Status'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Cheques;
